import { API } from "@swivl-mono/api-helper";
import { FeatureFlagKey } from "@swivl-mono/types";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { isFeatureEnabled } from "../../../../../Helpers/FeatureFlags/isFeatureEnabled";
import { Member } from "../../../../../Models/Member/Member.Current";

const POLICY_KEY = "access-procedures";
const UNMANNED_ITEM_KEY = "unmanned-facility";

interface UnmannedFacilityNoteProps {
  groupId?: number;
  locationId: number;
  /** Route prefix for the current scope (e.g. `/properties-v3/group/1/location/2`). */
  preamble: string;
}

/**
 * Whether the location is marked unmanned on the Access Procedures policy, with
 * inheritance resolved. `null` while loading or when the policy can't be read.
 */
function useIsUnmannedFacility(groupId: number | undefined, locationId: number): boolean | null {
  const orgId = Member.orgId;
  const [unmanned, setUnmanned] = useState<boolean | null>(null);

  useEffect(() => {
    if (!orgId || !isFeatureEnabled(FeatureFlagKey.PolicyBuilder)) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const { worksheets } = await API.PolicyWorksheets.getWorksheets(orgId, groupId, locationId);
        const worksheet = worksheets.find((w) => w.policyKey === POLICY_KEY);
        if (!cancelled) setUnmanned(worksheet ? worksheet.answers[UNMANNED_ITEM_KEY]?.value === "1" : null);
      } catch {
        if (!cancelled) setUnmanned(null);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [orgId, groupId, locationId]);

  return unmanned;
}

/**
 * Office Hours caption add-on (ENG-2461). The unmanned setting lives on the Access
 * Procedures policy, the single source of truth the bot reads; this note
 * points operators there from Hours, where office hours would otherwise
 * imply someone is on site. Renders nothing until the answer is known, or
 * when policies are off for the org (the link would have nowhere to go).
 */
export function UnmannedFacilityNote({ groupId, locationId, preamble }: UnmannedFacilityNoteProps): React.ReactElement | null {
  const unmanned = useIsUnmannedFacility(groupId, locationId);

  if (!isFeatureEnabled(FeatureFlagKey.PolicyBuilder) || unmanned === null) return null;

  const policyLink = (
    <Link
      to={`${preamble}/page/policy-worksheets?policyKey=${POLICY_KEY}`}
      className="font-medium text-orange-500 hover:text-orange-600 hover:underline"
    >
      Access Procedures policy
    </Link>
  );

  return (
    // Inline: it continues the Office Hours caption sentence, inheriting its style.
    <span>
      {unmanned ? (
        <>This facility is unmanned. Edit the {policyLink}.</>
      ) : (
        <>If this facility is unmanned, mark these as closed and update the {policyLink}.</>
      )}
    </span>
  );
}
